// src/pages/About.jsx
import { useTranslation, Trans } from "react-i18next";
import LocalizedLink from "../components/LocalizedLink";
import TeamSection from "../components/TeamSection";
import LogoBracket from "../components/LogoCircuit";

export default function AboutPage() {
  const { t } = useTranslation();

  const points = t("about.points", { returnObjects: true }) || [];
  const products = t("about.products", { returnObjects: true }) || [];
  const values = t("about.values", { returnObjects: true }) || [];

  return (
    <section className="mx-auto max-w-7xl px-4 py-16">
      {/* Hero */}
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
          {t("about.badge")}
          <span className="inline-block size-1 rounded-full bg-indigo-300" />
          {t("about.tagline")}
        </span>

        {/* Keep semantic H1 for SEO, but hide visually */}
        <h1 className="sr-only">{t("about.heading")}</h1>

        {/* Branded logo instead of visible H1 */}
        <div className="mt-4">
          <LogoBracket
            className="h-20 w-auto md:h-28 lg:h-32 block"
            dark="#0F172A"
            accent="#6366F1"
          />
        </div>

        <p className="mt-4 text-slate-600">{t("about.intro")}</p>
      </div>

      {/* What we do / Products / Stack */}
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {/* What we do */}
        <div className="rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold">
            {t("about.what_we_do_title")}
          </h2>

          <ul className="mt-4 space-y-2 text-sm text-slate-700">
            {points.map((p, i) => (
              <li key={i} className="relative pl-6">
                {/* consistent custom dot */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute left-0 top-2 inline-block h-2 w-2 rounded-full bg-indigo-600"
                />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Products we ship */}
        <div className="rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {t("about.products_title")}
            </h2>
            <LocalizedLink
              to="/applications"
              className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
            >
              {t("about.view_products")} →
            </LocalizedLink>
          </div>
          <ul className="mt-4 grid grid-cols-1 gap-2 text-sm text-slate-700">
            {products.map((name, i) => (
              <li
                key={i}
                className="rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200"
              >
                {name}
              </li>
            ))}
          </ul>
        </div>

        {/* Stack */}
        <div className="rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold">{t("about.stack_title")}</h2>
          <p className="mt-3 text-sm text-slate-700">{t("hero.tech_stack")}</p>
          <p className="mt-3 text-xs text-slate-500">{t("about.stack_note")}</p>
        </div>
      </div>

      {/* Values / How we work */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold">{t("about.values_title")}</h2>
        <p className="mt-2 text-slate-600">{t("about.values_intro")}</p>

        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          {values.map((v, i) => (
            <div
              key={i}
              className="rounded-2xl border border-slate-200 p-6 shadow-sm transition-transform duration-300 hover:scale-[1.01] hover:shadow-md"
            >
              <div className="text-sm font-semibold text-indigo-700">
                {v.title}
              </div>
              <div className="mt-2 text-slate-700">{v.desc}</div>
            </div>
          ))}
        </div>
      </div>
      <TeamSection />
      {/* CTA */}
      <div className="mt-12 flex flex-wrap gap-3">
        <LocalizedLink
          to="/contact"
          className="rounded-xl bg-indigo-600 px-5 py-3 text-white font-medium hover:bg-indigo-700"
        >
          {t("about.cta_primary")}
        </LocalizedLink>
        <LocalizedLink
          to="/projects"
          className="rounded-xl px-5 py-3 font-medium ring-1 ring-slate-300 hover:bg-slate-50"
        >
          {t("about.cta_secondary")}
        </LocalizedLink>
      </div>
    </section>
  );
}
